import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { coverFor, findProject, ownerLabel, projectPath, projects } from '../data/projects';
import { Eyebrow, Section } from '../ui/primitives';
import { colors } from '../ui/tokens';
import ProjectLinks from '../widgets/ProjectLinks';
import ProjectCard from '../widgets/ProjectCard';
import CtaBand from '../widgets/CtaBand';
import { NotFound } from '../errorpages/NotFound';

function Fact({ label, children }) {
    return (
        <Box sx={{ py: 2, borderTop: `1px solid ${colors.line}` }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: colors.muted, mb: 0.75 }}>
                {label}
            </Typography>
            <Box sx={{ color: colors.ink, fontSize: '0.97rem' }}>{children}</Box>
        </Box>
    );
}

function ProjectDetails() {
    const { slug } = useParams();
    const project = findProject(slug);
    const { t, i18n } = useTranslation('ProjectsDetails');
    const { t: tw } = useTranslation('OurWorkPage');
    const { t: tc } = useTranslation('Common');

    if (!project) {
        return <NotFound />;
    }
    // Old numeric URLs (/project/7) move to the readable one.
    if (slug !== project.slug) {
        return <Navigate to={projectPath(project)} replace />;
    }

    const p = (k, opts) => t(`${project.key}.${k}`, opts);
    const outcomes = p('outcomes', { returnObjects: true });
    const index = projects.indexOf(project);
    const next = projects[(index + 1) % projects.length];

    return (
        <>
            <Helmet>
                <title>{`${p('name')} · Oxide Digital Technology`}</title>
                <meta name="description" content={p('summary')} data-rh="true" />
                <link rel="canonical" href={`https://oxide-digital.com${projectPath(project)}`} />
            </Helmet>

            <Section tone="warm" py={{ xs: 5, md: 8 }}>
                <Box
                    component={Link}
                    to="/ourwork"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: colors.body, textDecoration: 'none', fontSize: '0.92rem', fontWeight: 600, mb: { xs: 4, md: 6 }, '&:hover': { color: colors.ink } }}
                >
                    <ArrowBackIcon sx={{ fontSize: '1rem' }} /> {tw('detail.back')}
                </Box>
                <Eyebrow>
                    {project.kind === 'product' ? tc('kind.product') : project.kind === 'private' ? tc('kind.private') : tc('kind.client')}
                    {' · '}
                    {p('category')}
                </Eyebrow>
                <Typography variant="h1" component="h1" sx={{ maxWidth: 900 }}>{p('headline')}</Typography>
                {project.links.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                        <ProjectLinks links={project.links} />
                    </Box>
                )}
            </Section>

            <Box sx={{ backgroundColor: colors.paperWarm }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2.5, sm: 4 } }}>
                    <Box
                        sx={{
                            borderRadius: '18px',
                            overflow: 'hidden',
                            backgroundColor: project.coverDark ? colors.navyDeep : colors.panel,
                            display: 'flex',
                            justifyContent: 'center',
                            transform: 'translateY(40px)',
                            boxShadow: '0 30px 60px -30px rgba(14,23,48,0.35)',
                        }}
                    >
                        <Box
                            component="img"
                            src={coverFor(project, i18n.language)}
                            alt={p('name')}
                            sx={{ width: '100%', height: 'auto', maxHeight: project.coverDark ? 'none' : 640, objectFit: 'contain', p: project.coverDark ? 0 : { xs: 2, md: 5 } }}
                        />
                    </Box>
                </Box>
            </Box>

            <Section tone="paper" py={{ xs: 10, md: 14 }}>
                <Grid container spacing={{ xs: 5, md: 10 }}>
                    <Grid item xs={12} md={8}>
                        <Eyebrow>{tw('detail.overview')}</Eyebrow>
                        <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.65, color: colors.ink }}>
                            {p('overview')}
                        </Typography>
                        {Array.isArray(outcomes) && (
                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 5 }}>
                                {outcomes.map((o) => (
                                    <Box component="li" key={o} sx={{ display: 'flex', gap: 1.5, py: 1.5, borderTop: `1px solid ${colors.line}` }}>
                                        <CheckIcon sx={{ color: colors.orangeDark, fontSize: '1.25rem', mt: 0.35 }} />
                                        <Typography sx={{ color: colors.ink, fontWeight: 500 }}>{o}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Fact label={tw('detail.client')}>{project.kind === 'private' ? tc('kind.privateClient') : ownerLabel(project, tc)}</Fact>
                        <Fact label={tw('detail.platforms')}>{project.platforms.join(' · ')}</Fact>
                        {project.stack.length > 0 && <Fact label={tw('detail.stack')}>{project.stack.join(' · ')}</Fact>}
                        {project.links.length > 0 ? (
                            <Fact label={tw('detail.links')}>
                                {project.links.map((l) => (
                                    <Box key={l.url} component="a" href={l.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block', color: colors.ink, py: 0.25 }}>
                                        {l.type === 'web' ? l.label : tc(`platforms.${l.type}`)}
                                    </Box>
                                ))}
                            </Fact>
                        ) : (
                            <Fact label={tw('detail.access')}>
                                {tw('detail.privateAccess')}{' '}
                                <Box component={Link} to="/contact" sx={{ color: colors.ink, fontWeight: 600 }}>{tw('detail.bookWalkthrough')}</Box>
                            </Fact>
                        )}
                    </Grid>
                </Grid>
            </Section>

            <Section tone="warm" py={{ xs: 9, md: 12 }}>
                <Eyebrow>{tw('detail.built')}</Eyebrow>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 8, md: 12 }, mt: { xs: 3, md: 5 } }}>
                    {project.gallery.map((item, i) => (
                        <Grid container spacing={{ xs: 3, md: 8 }} alignItems="center" key={item.section} direction={i % 2 === 1 ? 'row-reverse' : 'row'}>
                            <Grid item xs={12} md={7}>
                                <Box sx={{ borderRadius: '14px', overflow: 'hidden', backgroundColor: item.portrait ? colors.navyDeep : colors.panel, display: 'flex', justifyContent: 'center' }}>
                                    <Box
                                        component="img"
                                        src={item.image}
                                        alt={p(`sections.${item.section}.title`)}
                                        loading="lazy"
                                        sx={{ width: item.portrait ? 'auto' : '100%', maxHeight: item.portrait ? 560 : 'none', objectFit: 'contain', p: item.portrait || item.bleed ? 0 : { xs: 1.5, md: 3 } }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.orangeDark, mb: 1.5 }}>
                                    {String(i + 1).padStart(2, '0')}
                                </Typography>
                                <Typography variant="h3" component="h2" sx={{ mb: 2 }}>{p(`sections.${item.section}.title`)}</Typography>
                                <Typography sx={{ color: colors.body, fontSize: '1.02rem' }}>{p(`sections.${item.section}.body`)}</Typography>
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            </Section>

            <Section tone="paper" py={{ xs: 8, md: 11 }}>
                <Eyebrow>{tw('detail.next')}</Eyebrow>
                <Box sx={{ maxWidth: 760 }}>
                    <ProjectCard project={next} />
                </Box>
            </Section>

            <CtaBand />
        </>
    );
}

export { ProjectDetails };
